import {assertPartialSchema, createAction, useSelector, v} from '../../../lib'
import {
  UserDownloadInfo,
  UserDownload,
  UserDownloadProgress,
  UserDownloadResultSuccess,
  UserDownloadResultError,
  isUserDownloadResultSuccess,
} from '../../../model'
import {ArchiverProgress} from '../../../shared/data_download'
import {IdentityType} from '../../../shared/db'
import {CompletionProgress} from '../../../shared/queue'
import {toHydratedDate, toSerializableDate} from '../../serializable'

export enum UserDownloadActionType {
  USER_DOWNLOAD_SET = 'USER_DOWNLOAD_SET',
  USER_DOWNLOAD_DELETE = 'USER_DOWNLOAD_DELETE',
}

export const doUSER_DOWNLOAD_SET = createAction(UserDownloadActionType.USER_DOWNLOAD_SET)
export const doUSER_DOWNLOAD_DELETE = createAction(UserDownloadActionType.USER_DOWNLOAD_DELETE)

export const userDownloadActionCreators = {
  doUSER_DOWNLOAD_SET,
  doUSER_DOWNLOAD_DELETE,
}

type SerializableUserDownloadInfo = Omit<UserDownloadInfo, 'startDate' | 'endDate'> & {
  startDate?: string
  endDate?: string
}

type SerializableUserDownloadResultSuccess = Omit<
  UserDownloadResultSuccess,
  'createdAt' | 'executionStart' | 'registeredAt' | 'downloadedAt'
> & {
  createdAt: string
  executionStart: string
  registeredAt?: string | null
  downloadedAt?: string | null
}

type SerializableUserDownloadResult = SerializableUserDownloadResultSuccess | UserDownloadResultError | null

interface SerializableUserDownload {
  info: SerializableUserDownloadInfo
  result: SerializableUserDownloadResult
  progress: UserDownloadProgress | null
  startedAt?: string | null
}

interface UserDownloadState {
  [projectId: string]: SerializableUserDownload
}

interface RootState {
  userDownload: UserDownloadState
}

let cachedUserDownloadStateRaw: UserDownloadState | null = null
let cachedUserDownloadStateHydrated: Record<string, UserDownload> | null = null

export const selectUserDownload = () => {
  return useSelector((state: RootState) => hydrateUserDownloadState(state.userDownload ?? null))
}

export const userDownloadDefaultState: UserDownloadState = {}

type Action =
  | {
      type: UserDownloadActionType.USER_DOWNLOAD_SET
      payload: UserDownload
    }
  | {
      type: UserDownloadActionType.USER_DOWNLOAD_DELETE
      payload: {
        projectId: string
      }
    }

export const userDownloadReducer = (state = userDownloadDefaultState, {type, payload}: Action): UserDownloadState => {
  switch (type) {
    case UserDownloadActionType.USER_DOWNLOAD_SET: {
      assertPartialSchema({
        payload,
        schema: v.object<UserDownload>({
          info: v
            .object<UserDownloadInfo>({
              projectId: v.string().uuid().exist(),
              participantIds: v.array().items(v.string().uuid()).exist(),
              startDate: v.date().optional(),
              endDate: v.date().optional(),
            })
            .exist(),
          result: v
            .alternatives(
              v.object<UserDownloadResultSuccess>({
                url: v.alternatives(v.string(), v.array().items(v.string().exist())).exist(),
                createdAt: v.date().exist(),
                executionStart: v.date().exist(),
                inputHash: v.string(),
                registeredAt: v.date().optional().allow(null),
                downloadedAt: v.date().optional().allow(null),
                compressedSizeInBytes: v.number().optional().allow(null),
                uncompressedSizeInBytes: v.number().optional().allow(null),
                partsBreakdown: v
                  .array()
                  .items(
                    v.object({
                      partIndex: v.number().exist(),
                      key: v.string().exist(),
                      size: v.number().exist(),
                      sizeUncompressed: v.number().exist(),
                      participants: v
                        .array()
                        .items(
                          v.object({
                            id: v.string().exist(),
                            sizeUncompressed: v.number().exist(),
                          }),
                        )
                        .exist(),
                    }),
                  )
                  .optional(),
                requester: v
                  .object({
                    id: v.string().exist(),
                    type: v
                      .string()
                      .valid(...Object.values(IdentityType))
                      .exist(),
                    email: v.string().exist(),
                  })
                  .optional(),
              }),
              v.object<UserDownloadResultError>({
                errorMessage: v.string().exist(),
              }),
            )
            .allow(null)
            .exist(),
          progress: v
            .object<UserDownloadProgress>({
              tabulatorProgress: v
                .object<CompletionProgress>({
                  completed: v.number().exist(),
                  total: v.number().exist(),
                })
                .allow(null)
                .optional(),
              zipProgress: v
                .object<ArchiverProgress>({
                  waiting: v.number().exist(),
                  inProgress: v.number().exist(),
                  completed: v.number().exist(),
                  failed: v.number().exist(),
                  total: v.number().exist(),
                  inProgressParticipantId: v.string().optional(),
                  totalParticipants: v.number().optional(),
                  completedParticipants: v.number().optional(),
                })
                .allow(null)
                .optional(),
            })
            .allow(null)
            .exist(),
          startedAt: v.date().allow(null).optional(),
        }),
      })
      const newState = {...state}
      const {
        info: {projectId},
      } = payload
      newState[projectId] = normaliseUserDownload(payload)
      return newState
    }
    case UserDownloadActionType.USER_DOWNLOAD_DELETE: {
      assertPartialSchema({
        payload,
        schema: v
          .object({
            projectId: v.string().uuid().exist(),
          })
          .exist(),
      })
      const newState = {...state}
      const {projectId} = payload
      delete newState[projectId]
      return newState
    }
    default:
      return state
  }
}

export const hydrateUserDownloadState = (state: UserDownloadState | null): Record<string, UserDownload> | null => {
  if (!state) {
    return null
  }

  if (cachedUserDownloadStateRaw === state && cachedUserDownloadStateHydrated) {
    return cachedUserDownloadStateHydrated
  }

  const hydratedState = Object.entries(state).reduce(
    (result, [projectId, userDownload]) => {
      return {
        ...result,
        [projectId]: hydrateUserDownload(userDownload),
      }
    },
    {} as Record<string, UserDownload>,
  )

  cachedUserDownloadStateRaw = state
  cachedUserDownloadStateHydrated = hydratedState

  return hydratedState
}

function normaliseUserDownload(userDownload: UserDownload): SerializableUserDownload {
  return {
    ...userDownload,
    info: {
      ...userDownload.info,
      startDate: toSerializableDate(userDownload.info.startDate) as string | undefined,
      endDate: toSerializableDate(userDownload.info.endDate) as string | undefined,
    },
    result: normaliseUserDownloadResult(userDownload.result),
    startedAt: toSerializableDate(userDownload.startedAt) as string | null | undefined,
  }
}

function normaliseUserDownloadResult(result: UserDownload['result']): SerializableUserDownloadResult {
  if (!result) {
    return null
  }

  if (isUserDownloadResultSuccess(result)) {
    return {
      ...result,
      createdAt: toSerializableDate(result.createdAt) as string,
      executionStart: toSerializableDate(result.executionStart) as string,
      registeredAt: toSerializableDate(result.registeredAt) as string | null | undefined,
      downloadedAt: toSerializableDate(result.downloadedAt) as string | null | undefined,
    }
  }

  return result
}

function hydrateUserDownload(userDownload: SerializableUserDownload): UserDownload {
  return {
    ...userDownload,
    info: {
      ...userDownload.info,
      startDate: toHydratedDate(userDownload.info.startDate) as Date | undefined,
      endDate: toHydratedDate(userDownload.info.endDate) as Date | undefined,
    },
    result: hydrateUserDownloadResult(userDownload.result),
    startedAt: toHydratedDate(userDownload.startedAt) as Date | null | undefined,
  }
}

function hydrateUserDownloadResult(result: SerializableUserDownloadResult): UserDownload['result'] {
  if (!result) {
    return null
  }

  if (isSerializableUserDownloadResultSuccess(result)) {
    return {
      ...result,
      createdAt: toHydratedDate(result.createdAt) as Date,
      executionStart: toHydratedDate(result.executionStart) as Date,
      registeredAt: toHydratedDate(result.registeredAt ?? undefined) as Date,
      downloadedAt: toHydratedDate(result.downloadedAt ?? undefined) as Date | undefined,
    } as UserDownloadResultSuccess
  }

  return result as UserDownloadResultError
}

function isSerializableUserDownloadResultSuccess(
  result: SerializableUserDownloadResult,
): result is SerializableUserDownloadResultSuccess {
  if (!result) {
    return false
  }

  return 'url' in result
}
